import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
	CustomButton,
	CustomDialog,
	SidePanel,
	SidePanelContent,
	ViewTabPanel,
	ViewTabs,
	icon,
} from "../../../../common/components";
import { ApiService, CustomizableProjectPagesService, DistributionNotificationService } from "../../../../api";
import { useApi } from "../../../../common/hooks";
import { translate } from "../../../../common/providers";
import styles from "./DistributionNotificationSidePanel.module.css";
import NotificationForm from "./components/notification-form/NotificationForm";
import NotificationHistory from "./components/notification-history/NotificationHistory";
import InlineNotificationPreview from "./components/inline-notification-preview/InlineNotificationPreview";
import { generateSubject } from "../../utils/utils";

const TABS = [
	{ label: "document-center.distribution-notification.tab.form" },
	{ label: "document-center.distribution-notification.tab.history" },
];

const EMPTY_FORM = {
	documents: [],
	territories: [],
	recipientEmails: [],
	additionalSubject: "",
	distributionType: "",
	summary: "",
};

const getTerritoriesFromDraft = (draft) => {
	if (draft.territories && draft.territories.length > 0) {
		return draft.territories;
	}
	if (draft.defaultTerritory) {
		return [draft.defaultTerritory];
	}
	return (draft.territoryIds || []).map((id) => ({ id }));
};

export default function DistributionNotificationSidePanel({ open, onClose, onNotificationSent }) {
	const projectId = useSelector(({ context }) => context.project.id);
	const [selectedTab, setSelectedTab] = useState(0);
	const [previewMode, setPreviewMode] = useState(false);
	const [formData, setFormData] = useState(EMPTY_FORM);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [sending, setSending] = useState(false);
	const [draftLoaded, setDraftLoaded] = useState(false);
	const [defaultTerritoryId, setDefaultTerritoryId] = useState(null);
	const [contractName, setContractName] = useState("");
	const cancelTokenSourceRef = useRef(null);
	const { call: getCustomPageProjectDetails } = useApi(CustomizableProjectPagesService.getCustomPageProjectDetails);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	useEffect(() => {
		if (open && projectId && !draftLoaded) {
			DistributionNotificationService.getDraft(projectId, cancelTokenSourceRef.current?.token)
				.then((draft) => {
					if (draft) {
						const docs =
							draft.documents && draft.documents.length > 0 ? draft.documents : draft.documentIds || [];
						setFormData({
							documents: docs,
							territories: getTerritoriesFromDraft(draft),
							recipientEmails: draft.recipientEmails || [],
							additionalSubject: draft.additionalSubject || draft.subject || "",
							distributionType: draft.distributionType || "",
							summary: draft.summary || "",
						});
						if (draft.defaultTerritory?.id) {
							setDefaultTerritoryId(draft.defaultTerritory.id);
						}
					}
					setDraftLoaded(true);
				})
				.catch((err) => {
					console.error("Error loading draft:", err);
					setDraftLoaded(true);
				});
		}
	}, [open, projectId, draftLoaded]);

	useEffect(() => {
		if (!open) {
			setSelectedTab(0);
			setPreviewMode(false);
			setDraftLoaded(false);
			setContractName("");
		}
	}, [open]);

	useEffect(() => {
		if (open && projectId) {
			getCustomPageProjectDetails({ projectId })
				.then((data) => setContractName(data?.projectName || ""))
				.catch(console.error);
		}
	}, [open, projectId, getCustomPageProjectDetails]);

	const handleTabChange = (_, tab) => {
		setSelectedTab(tab);
		setPreviewMode(false);
	};

	const handleFormChange = useCallback((field, value) => setFormData((prev) => ({ ...prev, [field]: value })), []);

	const isFormValid = () =>
		formData.documents.length > 0 &&
		formData.territories.length > 0 &&
		formData.distributionType.trim() !== "" &&
		formData.summary.trim() !== "";

	const hasFormData = () =>
		formData.documents.length > 0 ||
		formData.territories.length > 0 ||
		formData.recipientEmails.length > 0 ||
		formData.additionalSubject.trim() !== "" ||
		formData.distributionType.trim() !== "" ||
		formData.summary.trim() !== "";

	const handlePreview = () => {
		setPreviewMode(true);
	};

	const handleBackToEdit = () => {
		setPreviewMode(false);
	};

	const handleSendClick = () => {
		if (isFormValid()) {
			setConfirmDialogOpen(true);
		}
	};

	const saveDraftSilently = () => {
		if (!hasFormData()) {
			DistributionNotificationService.deleteDraft?.(projectId, cancelTokenSourceRef.current?.token).catch(
				console.error
			);
			return;
		}
		const documentsWithNames = formData.documents.map((d) =>
			typeof d === "object"
				? { id: d.id, name: d.name || d.label || `Document ${d.id}` }
				: { id: d, name: `Document ${d}` }
		);
		const payload = {
			projectId,
			territoryIds: formData.territories.map((t) => t.id),
			subject: formData.additionalSubject,
			summary: formData.summary,
			distributionType: formData.distributionType,
			documentIds: documentsWithNames.map((d) => d.id),
			documents: documentsWithNames,
			recipientEmails: formData.recipientEmails,
		};
		DistributionNotificationService.saveDraft(projectId, payload, cancelTokenSourceRef.current?.token).catch(
			(err) => console.error("Error saving draft:", err)
		);
	};

	const handleConfirmSend = () => {
		setSending(true);
		if (onNotificationSent) {
			onNotificationSent();
		}
		const payload = {
			projectId,
			territoryIds: formData.territories.map((t) => t.id),
			subject: generateSubject(contractName, formData.additionalSubject),
			summary: formData.summary,
			distributionType: formData.distributionType,
			documentIds: formData.documents.map((d) => (typeof d === "object" ? d.id : d)),
			recipientEmails: formData.recipientEmails,
		};

		DistributionNotificationService.send(projectId, payload, cancelTokenSourceRef.current?.token)
			.then(() => {
				setConfirmDialogOpen(false);
				setFormData(EMPTY_FORM);
				setPreviewMode(false);
				onClose();
			})
			.catch((err) => {
				console.error("Error sending notification:", err);
			})
			.finally(() => {
				setSending(false);
			});
	};

	const handleCloseConfirmDialog = () => {
		setConfirmDialogOpen(false);
	};

	const handleClose = () => {
		saveDraftSilently();
		setPreviewMode(false);
		onClose();
	};

	const translatedTabs = TABS.map((tab) => ({
		...tab,
		label: translate(tab.label),
	}));

	const previewData = {
		subject: generateSubject(contractName, formData.additionalSubject),
		documents: formData.documents,
		distributionType: formData.distributionType,
		summary: formData.summary,
		territoryName: formData.territories.map((t) => t.name).join(", "),
		recipientEmails: formData.recipientEmails,
	};

	return (
		<SidePanel offset={10} open={open} size={50} onClose={handleClose}>
			<SidePanelContent title={translate("document-center.distribution-notification")} onClose={handleClose}>
				<div className={styles.wrapper}>
					<div className={`${styles.content} ${selectedTab === 1 ? styles.contentNoScroll : ""}`}>
						<div className={styles.tabsWrapper}>
							<ViewTabs
								noPadding
								noShadow
								selectedTab={selectedTab}
								tabs={translatedTabs}
								onChange={handleTabChange}
							/>
						</div>
						<ViewTabPanel noPadding index={0} value={selectedTab}>
							{previewMode ? (
								<InlineNotificationPreview data={previewData} />
							) : (
								<NotificationForm
									defaultTerritoryId={defaultTerritoryId}
									formData={formData}
									projectId={projectId}
									projectName={contractName}
									onFormChange={handleFormChange}
								/>
							)}
						</ViewTabPanel>

						<ViewTabPanel noPadding className={styles.historyTabPanel} index={1} value={selectedTab}>
							<NotificationHistory projectId={projectId} />
						</ViewTabPanel>
					</div>

					{selectedTab === 0 && (
						<div className={styles.footer}>
							{previewMode ? (
								<>
									<CustomButton color="secondary" variant="outlined" onClick={handleBackToEdit}>
										{translate("common:btn.edit")}
									</CustomButton>
									<CustomButton
										color="primary"
										disabled={!isFormValid() || sending}
										variant="contained"
										onClick={handleSendClick}
									>
										{translate("document-center.distribution-notification.btn.send")}
									</CustomButton>
								</>
							) : (
								<>
									<CustomButton
										color="secondary"
										disabled={!isFormValid()}
										variant="outlined"
										onClick={handlePreview}
									>
										{translate("document-center.distribution-notification.btn.preview")}
									</CustomButton>
									<CustomButton
										color="primary"
										disabled={!isFormValid() || sending}
										variant="contained"
										onClick={handleSendClick}
									>
										{translate("document-center.distribution-notification.btn.send")}
									</CustomButton>
								</>
							)}
						</div>
					)}
				</div>

				<CustomDialog
					iconColor="var(--red-500)"
					iconModel={icon.faExclamationTriangle}
					open={confirmDialogOpen}
					submitLabel={translate("common:btn.confirm")}
					subTitle={translate("document-center.distribution-notification.confirm.message-with-details")}
					onClose={handleCloseConfirmDialog}
					onSubmit={handleConfirmSend}
				/>
			</SidePanelContent>
		</SidePanel>
	);
}
